/**
 * The Fields tab — the document's SCHEMA, grouped by the form each field
 * belongs to.
 *
 * It used to be one ten-column spreadsheet: `Key · Title · Kind · Type · Req ·
 * In table · Writes to · Formula · Bound to`, the last of those a textarea of
 * raw binding JSON. Every field cost ten cells whether or not they applied to
 * it, the form a field wrote to was a dotted path you had to decode, and
 * reading down the page told you nothing about what the document actually did.
 *
 * Three things replace it, and each is a rule the engine already holds:
 *
 *  * **A field belongs to one form.** §6g says everything names its place as
 *    `TXN.FORM.COLUMN`, so the list is GROUPED that way — headed by the form's
 *    own heading, as its transaction names it, with the form number as a badge
 *    inside the group. `fieldGroups` is the whole rule and is pure.
 *  * **A value arrives one of six ways.** `entry` / a `context` merge / a
 *    `rule` merge / `calc` / `repeat` / `signature` are the model's own
 *    vocabulary; the row states which in a sentence, and opening a field
 *    offers those six and then only the settings that apply.
 *  * **Resolving is a review pass.** The preview's values land on the rows
 *    they belong to instead of arriving as one `JSON.stringify` block.
 *
 * What is NOT lost: every field keeps an **Advanced** disclosure with its raw
 * `binding` JSON, `map`, `path`, `in_table` and `calc`. The structured editors
 * MERGE into a binding rather than replacing it, so a key this designer was
 * never taught about survives a save — which is the reason the old textarea
 * existed and the one thing a structured editor must not take away.
 */

import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { previewDocumentForm } from '@/api/er'
import {
  CRITERIA_GROUP, DOC_DATA_GROUP, TYPE_WORDS, buildFieldGroups,
  describeSource, parsePath, sourceOf, type FieldGroup, type SourceKind,
} from './fieldGroups'
import type { SourceColumn } from './sources'
import { buttonStyle, inputStyle } from './styles'
import { BoundStoredIn, StoredInContext } from './StoredIn'
import { destinationIn, typeIsOwned } from './destination'
import type { FieldRow, FormModel, FormTemplate } from './types'
import { useTxnCatalog } from './useTxnCatalog'

type KindFilter = 'all' | 'entry' | 'auto' | 'sign'

interface PreviewResult {
  values?: Record<string, unknown>
  unresolved?: { field: string; title: string; message: string }[]
  denied?: { field: string; title: string; message: string }[]
}

/** How a resolved field came back, for the pill on its row. */
type Verdict = 'ok' | 'wait' | 'bad'

const SOURCE_LABELS: Record<SourceKind, string> = {
  entry: 'Typed in',
  context: 'From the record',
  rule: 'Looked up',
  calc: 'Worked out',
  repeat: 'A repeating table',
  sign: 'Signed',
}

const SOURCE_ORDER: SourceKind[] = [
  'entry', 'context', 'rule', 'calc', 'repeat', 'sign',
]

const ICONS: Record<string, string> = {
  entry: 'M4 16h3.2l8.1-8.1a1.7 1.7 0 0 0 0-2.4l-.8-.8a1.7 1.7 0 0 0-2.4 0L4 12.8V16z',
  context: 'M11.5 2.5H6A1.5 1.5 0 0 0 4.5 4v12A1.5 1.5 0 0 0 6 17.5h8a1.5 1.5 0 0 0 1.5-1.5V6.5l-4-4zM11.5 2.5v4h4M7.5 11.5h5M7.5 14h3',
  rule: 'M9 15.5a6.5 6.5 0 1 0 0-13 6.5 6.5 0 0 0 0 13zM17.5 17.5l-3.9-3.9',
  calc: 'M4.5 3.5h11a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1v-11a1 1 0 0 1 1-1zM7 8.5h6M7 11.5h6',
  repeat: 'M3.5 4.5h13v11h-13zM3.5 8.5h13M3.5 12h13M8 4.5v11',
  sign: 'M3 14.5c2.5 0 3-8 5.2-8 1.6 0 1.3 5 3 5 1.4 0 1.7-3 3-3 1 0 1.3 2 2.8 2M3 17.5h14',
  header: 'M3.5 4.5h13v11h-13zM3.5 8.5h13',
  own: 'M10 2.8l6.2 3.1v5c0 3.4-2.6 5.8-6.2 6.9-3.6-1.1-6.2-3.5-6.2-6.9v-5z',
  lock: 'M5.8 9V6.8a4.2 4.2 0 0 1 8.4 0V9M4.8 9h10.4v7.2H4.8z',
}

function Icon({ path, size = 16, tone = 'var(--color-text-secondary)' }: {
  path: string; size?: number; tone?: string
}) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none"
      stroke={tone} strokeWidth={1.6} strokeLinecap="round"
      strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d={path} />
    </svg>
  )
}

export default function FieldsTab({
  template, templateIndex, mutate, project, objName, model, objects,
}: {
  template: FormTemplate
  /** WHICH template is being edited — a composed form has several. */
  templateIndex: number
  mutate: (fn: (m: FormModel) => void) => void
  project: string
  objName: string
  model: FormModel
  objects: { obj_name: string }[]
}) {
  const [openKey, setOpenKey] = useState<string | null>(null)
  const [kindFilter, setKindFilter] = useState<KindFilter>('all')
  const [formFilter, setFormFilter] = useState<string>('all')
  const [advanced, setAdvanced] = useState<Record<string, boolean>>({})
  const [preview, setPreview] = useState<PreviewResult | null>(null)
  const [previewTxn, setPreviewTxn] = useState('')
  const [previewBusy, setPreviewBusy] = useState(false)

  const txns = model.form.transactions || []
  const { catalog, farForms, loadObject } = useTxnCatalog(project, txns)
  const groups = useMemo(
    () => buildFieldGroups(template.fields || [], txns, catalog),
    [template.fields, txns, catalog])

  // ── mutations, all keyed by the field's position in `template.fields` ────
  const patch = useCallback((index: number, fn: (f: FieldRow) => void) => {
    mutate((m) => { fn(m.form.templates[templateIndex].fields[index]) })
  }, [mutate])

  const removeField = useCallback((index: number) => {
    mutate((m) => { m.form.templates[templateIndex].fields.splice(index, 1) })
  }, [mutate])

  const duplicateField = useCallback((index: number) => {
    const key = nextKey(template.fields || [])
    mutate((m) => {
      const fields = m.form.templates[templateIndex].fields
      const copy = JSON.parse(JSON.stringify(fields[index])) as FieldRow
      copy.key = key
      copy.title = copy.title ? `${copy.title} (copy)` : ''
      // The map named the ORIGINAL's column. Point the copy at a column of its
      // own name on the same form, so the two never write to one place.
      const [txn, formNo, column] = parsePath(copy.map)
      if (txn && column) copy.map = `${txn}.${formNo || '1'}.${key}`
      fields.splice(index + 1, 0, copy)
    })
    setOpenKey(key)
  }, [mutate, template.fields])

  const addFieldTo = useCallback((group: FieldGroup) => {
    const key = nextKey(template.fields || [])
    mutate((m) => {
      const fields = m.form.templates[templateIndex].fields
      const row: FieldRow = {
        key, title: '', kind: group.own ? 'calc' : 'entry',
        data_type: 'VARCHAR', mandatory: false, in_table: '',
        binding: null, tolerance: null,
        map: group.own ? '' : `${group.txnIdent}.${group.formNo}.${key}`,
      }
      fields.push(row)
    })
    setKindFilter('all')
    setOpenKey(key)
  }, [mutate, template.fields])

  /**
   * Change how a field gets its value.
   *
   * What identifies the field — key, title, type, mandatory, where it is saved,
   * the table it is a column of — survives; only the parts that belong to the
   * OLD source are cleared, because a stale `calc` on a field that is now typed
   * in is a formula the engine still evaluates.
   */
  const setSource = useCallback((index: number, next: SourceKind) => {
    patch(index, (f) => {
      if (next === 'entry') {
        f.kind = 'entry'; f.binding = null
        delete f.calc; delete f.path; delete f.order_by; delete f.filter
      } else if (next === 'context') {
        f.kind = 'merge'
        const mode = String((f.binding as { mode?: unknown } | null)?.mode || '')
        if (mode === 'rule') f.binding = null
        delete f.calc; delete f.path
      } else if (next === 'rule') {
        f.kind = 'merge'
        const mode = String((f.binding as { mode?: unknown } | null)?.mode || '')
        if (mode !== 'rule') {
          f.binding = {
            mode: 'rule', source_object: '', source_form: '1',
            match_column: '', match_value: '', fill: [],
            on_no_match: 'blank', on_multi_match: 'error',
          }
        }
        delete f.calc; delete f.path
      } else if (next === 'calc') {
        f.kind = 'calc'; f.binding = null
        f.calc = f.calc || ''
        delete f.path
      } else if (next === 'repeat') {
        f.kind = 'repeat'; f.binding = null
        f.path = f.path || ''
        delete f.calc
      } else {
        f.kind = 'signature'; f.binding = null
        delete f.calc; delete f.path
      }
    })
  }, [patch])

  const runPreview = async () => {
    setPreviewBusy(true)
    try {
      const res = await previewDocumentForm({
        project, objName, txnId: previewTxn || undefined,
        form: model.form as unknown as Record<string, unknown>,
      })
      setPreview(res)
    } catch (exc) {
      const detail = (exc as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail
      toast.error(detail || 'The bindings could not be resolved.')
    } finally {
      setPreviewBusy(false)
    }
  }

  // ── what the chips count, and what they let through ──────────────────────
  const counts = { all: 0, entry: 0, auto: 0, sign: 0 }
  ;(template.fields || []).forEach((f) => {
    const kind = sourceOf(f)
    counts.all += 1
    if (kind === 'entry') counts.entry += 1
    else if (kind === 'sign') counts.sign += 1
    else counts.auto += 1
  })

  const passes = (field: FieldRow) => {
    const kind = sourceOf(field)
    if (kindFilter === 'all') return true
    if (kindFilter === 'entry') return kind === 'entry'
    if (kindFilter === 'sign') return kind === 'sign'
    return kind !== 'entry' && kind !== 'sign'
  }

  const shown = groups.filter((g) => formFilter === 'all' || formFilter === g.id)
  const verdicts = verdictsOf(preview, template.fields || [])

  if (!(template.fields || []).length && !groups.length) {
    return (
      <p style={{ fontSize: '0.9625rem', color: 'var(--color-text-secondary)' }}>
        No fields yet, and no transaction to hang them on. Cover a transaction
        on the Settings tab first — a field belongs to one of its forms.
      </p>
    )
  }

  return (
    <StoredInContext.Provider value={{ model, catalog, templateIndex }}>
    <div style={{ display: 'grid', gap: 12 }}>

      {/* ── toolbar ──────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8,
        flexWrap: 'wrap' }}>
        <button type="button" style={buttonStyle(true)}
          disabled={!groups.length}
          onClick={() => {
            const target = shown[0] || groups[0]
            if (target) addFieldTo(target)
          }}>
          Add a field
        </button>
        <span style={{ width: 1, height: 20, background: 'var(--color-border)',
          margin: '0 2px' }} />
        {([
          ['all', 'Every field'], ['entry', 'Typed in'],
          ['auto', 'Filled in for you'], ['sign', 'Signed'],
        ] as [KindFilter, string][]).map(([id, label]) => (
          <button key={id} type="button" onClick={() => setKindFilter(id)}
            style={chipStyle(kindFilter === id)}>
            {label}
            <span style={{ opacity: 0.7, fontVariantNumeric: 'tabular-nums' }}>
              {counts[id]}
            </span>
          </button>
        ))}
        <span style={{ flex: 1 }} />
        <input value={previewTxn} onChange={(e) => setPreviewTxn(e.target.value)}
          placeholder="a record to try it on"
          style={{ ...inputStyle, width: 190 }} />
        <button type="button" onClick={() => void runPreview()}
          disabled={previewBusy} style={buttonStyle(false)}>
          {previewBusy ? 'Resolving…' : 'Try it on a real record'}
        </button>
        {preview && (
          <button type="button" onClick={() => setPreview(null)}
            style={buttonStyle(false)}>Clear</button>
        )}
      </div>

      <PreviewSummary preview={preview} verdicts={verdicts}
        total={counts.all} forms={groups.filter((g) => !g.own).length} />

      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>

        {/* ── what this document covers ───────────────────────────────── */}
        <div style={{ width: 236, flexShrink: 0, borderRadius: 8,
          border: '1px solid var(--color-border)',
          background: 'var(--color-bg-secondary)', overflow: 'hidden' }}>
          <div style={{ ...microLabel, padding: '9px 12px',
            borderBottom: '1px solid var(--color-border)' }}>
            WHAT THIS DOCUMENT COVERS
          </div>
          {groups.map((g) => (
            <div key={g.id}>
              {g.firstOfTxn && (
                <div style={{ padding: '10px 12px 3px', display: 'grid',
                  gap: 1 }}>
                  <span style={{ fontSize: '0.8925rem', fontWeight: 600 }}>
                    {g.txnTitle}
                  </span>
                  {!g.own && (
                    <span style={{ fontSize: '0.8125rem',
                      color: 'var(--color-text-faint)' }}>
                      {g.readOnly ? 'linked, read only' : g.txnIdent}
                    </span>
                  )}
                </div>
              )}
              <button type="button"
                onClick={() => setFormFilter(formFilter === g.id ? 'all' : g.id)}
                style={{
                  width: 'calc(100% - 12px)', margin: '2px 6px',
                  padding: '6px 8px', borderRadius: 6, cursor: 'pointer',
                  border: '1px solid transparent', textAlign: 'left',
                  display: 'flex', alignItems: 'center', gap: 7,
                  background: formFilter === g.id
                    ? 'var(--color-accent-weak)' : 'transparent',
                  color: formFilter === g.id
                    ? 'var(--color-accent)' : 'var(--color-text)',
                }}>
                <Icon path={g.own ? ICONS.own
                  : (g.detail ? ICONS.repeat : ICONS.header)} size={14}
                  tone={formFilter === g.id
                    ? 'var(--color-accent)' : 'var(--color-text-secondary)'} />
                <span style={{ fontSize: '0.8825rem' }}>{g.heading}</span>
                {!g.own && <span style={badgeStyle}>{g.formNo}</span>}
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: '0.8425rem',
                  color: 'var(--color-text-faint)',
                  fontVariantNumeric: 'tabular-nums' }}>
                  {g.entries.length}
                </span>
              </button>
            </div>
          ))}
          <p style={{ ...hintStyle, padding: '8px 12px 12px', marginTop: 4,
            borderTop: '1px solid var(--color-border)' }}>
            A field always belongs to one form. Move it by changing where it is
            saved.
          </p>
        </div>

        {/* ── the fields, form by form ────────────────────────────────── */}
        <div style={{ flex: 1, minWidth: 0, display: 'grid', gap: 12 }}>
          {shown.map((group) => (
            <GroupCard key={group.id} group={group} openKey={openKey}
              setOpenKey={setOpenKey} passes={passes} verdicts={verdicts}
              showVerdicts={!!preview} patch={patch} setSource={setSource}
              removeField={removeField} duplicateField={duplicateField}
              addFieldTo={addFieldTo}
              advanced={advanced} setAdvanced={setAdvanced}
              groups={groups} objects={objects} farForms={farForms}
              loadObject={loadObject} fields={template.fields || []}
              catalog={catalog} />
          ))}
        </div>
      </div>
    </div>
    </StoredInContext.Provider>
  )
}

// ── one form's worth of fields ─────────────────────────────────────────────

function GroupCard(props: {
  group: FieldGroup
  openKey: string | null
  setOpenKey: (k: string | null) => void
  passes: (f: FieldRow) => boolean
  verdicts: Record<string, { verdict: Verdict; text: string }>
  showVerdicts: boolean
  patch: (index: number, fn: (f: FieldRow) => void) => void
  setSource: (index: number, next: SourceKind) => void
  removeField: (index: number) => void
  duplicateField: (index: number) => void
  addFieldTo: (g: FieldGroup) => void
  advanced: Record<string, boolean>
  setAdvanced: (v: Record<string, boolean>) => void
  groups: FieldGroup[]
  objects: { obj_name: string }[]
  farForms: Record<string, { formNo: string; title: string;
    detail: boolean; columns: SourceColumn[] }[]>
  loadObject: (name: string) => void
  fields: FieldRow[]
  catalog: { ident: string; title: string;
    forms: { formNo: string; title: string; detail: boolean;
      columns: SourceColumn[] }[] }[]
}) {
  const { group, verdicts, showVerdicts } = props
  const visible = group.entries.filter((e) => props.passes(e.field))

  return (
    <section style={{ borderRadius: 8, overflow: 'hidden',
      border: '1px solid var(--color-border)',
      background: 'var(--color-bg-secondary)' }}>

      <header style={{ display: 'flex', alignItems: 'center', gap: 9,
        padding: '9px 12px', borderBottom: '1px solid var(--color-border)',
        background: 'var(--color-surface-2)', flexWrap: 'wrap' }}>
        <Icon path={group.own ? ICONS.own
          : (group.readOnly ? ICONS.lock
            : (group.detail && !group.setHeader
              ? ICONS.repeat : ICONS.header))}
          tone="var(--color-text)" />
        <strong style={{ fontSize: '0.9825rem' }}>{group.heading}</strong>
        {!group.own && <span style={badgeStyle}>{group.formBadge}</span>}
        <span style={{ fontSize: '0.8925rem', minWidth: 120, flexShrink: 1,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          color: 'var(--color-text-secondary)' }}>
          {group.id === CRITERIA_GROUP
            ? 'answered at the prompt — the same value on every page, saved to nothing'
            : group.id === DOC_DATA_GROUP
              ? 'typed on the form and kept with it — saved to no transaction column'
              : group.own
                ? 'worked out here, or signed here — saved to no transaction'
                : group.txnTitle + (group.readOnly ? ' · read only' : '')}
        </span>
        {group.repeatTitle && (
          <span style={{ ...badgeStyle, color: 'var(--color-accent)',
            borderColor: 'var(--color-accent)',
            background: 'var(--color-accent-weak)' }}>
            REPEATS AS “{group.repeatTitle}”
          </span>
        )}
        {group.unresolved && (
          <span style={{ ...badgeStyle, color: '#b45309' }}>
            MODEL NOT LOADED
          </span>
        )}
        <span style={{ flex: 1 }} />
        {!group.own && (
          <code style={{ fontSize: '0.8125rem',
            color: 'var(--color-text-faint)' }}>{group.id}</code>
        )}
        <span style={{ fontSize: '0.8425rem',
          color: 'var(--color-text-faint)' }}>
          {group.entries.length === 1 ? '1 field'
            : `${group.entries.length} fields`}
        </span>
      </header>

      {visible.length > 0 && (
        /*
         * The header and the rows are one grid, and every fixed cell says so.
         *
         * Each of these used to be a flex item with a `width` and no
         * `flexShrink: 0`, which is a MAXIMUM, not a width: as soon as the
         * sentence in the middle column ran long the browser took the
         * difference out of HOLDS and REQUIRED, the row's cells slid left of
         * the headings above them, and "Required" printed over the type word.
         */
        <div style={{ display: 'flex', alignItems: 'center', gap: 10,
          padding: '5px 12px', ...microLabel,
          borderBottom: '1px solid var(--color-border)' }}>
          <span style={{ width: 188, flexShrink: 0 }}>FIELD</span>
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden',
            textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {group.detail && !group.setHeader
              ? 'WHERE EACH ROW GETS ITS VALUE'
              : 'WHERE ITS VALUE COMES FROM'}
          </span>
          {showVerdicts && (
            <span style={{ width: 200, flexShrink: 0 }}>WHAT CAME BACK</span>
          )}
          <span style={{ width: 74, flexShrink: 0 }}>HOLDS</span>
          <span style={{ width: 70, flexShrink: 0 }}>REQUIRED</span>
          <span style={{ width: 18, flexShrink: 0 }} />
        </div>
      )}

      {visible.map(({ field, index }) => (
        <FieldRowView key={`${field.key}-${index}`} field={field} index={index}
          group={group} verdict={verdicts[field.key]}
          showVerdict={showVerdicts}
          open={props.openKey === field.key}
          onToggle={() => props.setOpenKey(
            props.openKey === field.key ? null : field.key)}
          patch={props.patch} setSource={props.setSource}
          removeField={props.removeField}
          duplicateField={props.duplicateField}
          advanced={!!props.advanced[field.key]}
          setAdvanced={(v) => props.setAdvanced({
            ...props.advanced, [field.key]: v })}
          groups={props.groups} objects={props.objects}
          farForms={props.farForms} loadObject={props.loadObject}
          fields={props.fields} catalog={props.catalog} />
      ))}

      {!visible.length && (
        <p style={{ ...hintStyle, padding: '12px' }}>
          {group.entries.length
            ? 'No field on this form matches the filter above.'
            : 'No fields on this form yet.'}
        </p>
      )}

      {/* Nothing to add HERE. A criterion exists because the visual asks for
          it; inventing one on the Fields tab would declare a prompt nobody
          answers. They are placed from the Body tab's rail, which lists the
          ones the run actually has. */}
      {group.id !== CRITERIA_GROUP && (
        <button type="button" onClick={() => props.addFieldTo(group)}
          style={{ display: 'flex', alignItems: 'center', gap: 7,
            padding: '9px 12px', border: 'none', background: 'transparent',
            color: 'var(--color-accent)', fontSize: '0.8925rem',
            cursor: 'pointer', fontWeight: 500 }}>
          + {group.id === DOC_DATA_GROUP
            ? 'Add something to be filled in'
            : group.own ? 'Add a field the document works out itself'
              : `Add a field to ${group.heading}`}
        </button>
      )}
    </section>
  )
}

// ── one field ──────────────────────────────────────────────────────────────

function FieldRowView(props: {
  field: FieldRow
  index: number
  group: FieldGroup
  verdict?: { verdict: Verdict; text: string }
  showVerdict: boolean
  open: boolean
  onToggle: () => void
  patch: (index: number, fn: (f: FieldRow) => void) => void
  setSource: (index: number, next: SourceKind) => void
  removeField: (index: number) => void
  duplicateField: (index: number) => void
  advanced: boolean
  setAdvanced: (v: boolean) => void
  groups: FieldGroup[]
  objects: { obj_name: string }[]
  farForms: Record<string, { formNo: string; title: string;
    detail: boolean; columns: SourceColumn[] }[]>
  loadObject: (name: string) => void
  fields: FieldRow[]
  catalog: { ident: string; title: string;
    forms: { formNo: string; title: string; detail: boolean;
      columns: SourceColumn[] }[] }[]
}) {
  const { field, index, group, open } = props
  const kind = sourceOf(field)
  const [lead, strong, tail] = describeSource(field, group)
  const typeWord = TYPE_WORDS[String(field.data_type || '').toUpperCase()]
    || field.data_type

  return (
    <div style={{ borderBottom: '1px solid var(--color-border)',
      background: open ? 'var(--color-surface-2)' : 'transparent' }}>

      <div onClick={props.onToggle} role="button" tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter') props.onToggle() }}
        style={{ display: 'flex', alignItems: 'center', gap: 10,
          padding: '8px 12px', cursor: 'pointer' }}>

        <div style={{ width: 188, flexShrink: 0, minWidth: 0,
          display: 'grid', gap: 1 }}>
          <span style={{ fontSize: '0.9625rem', fontWeight: 600,
            overflow: 'hidden', textOverflow: 'ellipsis',
            whiteSpace: 'nowrap' }}>
            {field.title || <em style={{ color: 'var(--color-text-faint)',
              fontWeight: 400 }}>Untitled field</em>}
          </span>
          <code style={{ fontSize: '0.8125rem', overflow: 'hidden',
            textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            color: 'var(--color-text-faint)' }}>{field.key}</code>
        </div>

        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8,
          minWidth: 0 }}>
          <Icon path={ICONS[kind]} size={16} />
          {/* One line, clipped. The sentence is a summary — the field opens
              for the detail — and letting it run was what pushed the three
              columns to its right out from under their own headings. */}
          <span style={{ fontSize: '0.9125rem', minWidth: 0,
            overflow: 'hidden', textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: 'var(--color-text-secondary)' }}>
            {lead}{' '}
            {strong && <strong style={{ color: 'var(--color-text)' }}>
              {strong}</strong>}{' '}
            {tail}
          </span>
        </div>

        {props.showVerdict && (
          <div style={{ width: 200, flexShrink: 0, minWidth: 0 }}>
            {props.verdict && (
              <span style={verdictStyle(props.verdict.verdict)}>
                {props.verdict.text}
              </span>
            )}
          </div>
        )}

        <span style={{ width: 74, flexShrink: 0, fontSize: '0.8925rem',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          color: 'var(--color-text-secondary)' }}>{typeWord}</span>

        <span style={{ width: 70, flexShrink: 0 }}>
          {field.mandatory && (
            <span style={{ padding: '1px 7px', borderRadius: 999,
              fontSize: '0.8125rem', fontWeight: 600,
              background: 'var(--color-accent-weak)',
              color: 'var(--color-accent)' }}>Required</span>
          )}
        </span>

        <svg width={16} height={16} viewBox="0 0 20 20" fill="none"
          stroke="var(--color-text-faint)" strokeWidth={1.8}
          strokeLinecap="round" strokeLinejoin="round"
          style={{ width: 18, flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'none' }}>
          <path d="M5.5 8l4.5 4.5L14.5 8" />
        </svg>
      </div>

      {open && (
        <div style={{ padding: '4px 12px 16px 34px' }}>
          <div style={{ ...microLabel, margin: '8px 0 7px' }}>
            WHERE DOES THIS VALUE COME FROM?
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {SOURCE_ORDER.map((id) => {
              // A read-only transaction may be SHOWN on the document and never
              // written, so nothing on its forms can be typed or signed.
              const barred = group.readOnly && (id === 'entry' || id === 'sign')
              const on = id === kind
              return (
                <button key={id} type="button" disabled={barred}
                  onClick={() => props.setSource(index, id)}
                  title={barred
                    ? 'This transaction is read only — the document may show '
                      + 'its columns but never write them.' : undefined}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 7,
                    padding: '6px 10px', borderRadius: 7,
                    cursor: barred ? 'not-allowed' : 'pointer',
                    opacity: barred ? 0.45 : 1,
                    border: `1px solid ${on ? 'var(--color-accent)'
                      : 'var(--color-border)'}`,
                    boxShadow: on ? 'inset 0 0 0 1px var(--color-accent)'
                      : 'none',
                    background: 'var(--color-bg)',
                    color: on ? 'var(--color-accent)' : 'var(--color-text)',
                    fontWeight: on ? 600 : 400, fontSize: '0.9025rem',
                  }}>
                  <Icon path={ICONS[id]} size={15}
                    tone={on ? 'var(--color-accent)'
                      : 'var(--color-text-secondary)'} />
                  {SOURCE_LABELS[id]}
                </button>
              )
            })}
          </div>

          <div style={{ marginTop: 12, padding: '14px 14px 16px',
            borderRadius: 8, border: '1px solid var(--color-border)',
            background: 'var(--color-bg)', display: 'grid', gap: 14 }}>

            <Line>
              <span>Called</span>
              <input value={field.title || ''} placeholder="its heading"
                onChange={(e) => props.patch(index,
                  (f) => { f.title = e.target.value })}
                style={{ ...inputStyle, width: 220 }} />
              <span>and referred to in the body as</span>
              <input value={field.key}
                onChange={(e) => props.patch(index,
                  (f) => { f.key = e.target.value.toUpperCase() })}
                style={{ ...inputStyle, width: 170,
                  fontFamily: 'var(--font-mono)' }} />
            </Line>

            {kind === 'entry' && (
              <EntryEditor field={field} index={index} group={group}
                groups={props.groups} patch={props.patch} />
            )}
            {kind === 'context' && (
              <ContextEditor field={field} index={index} group={group}
                groups={props.groups} patch={props.patch} />
            )}
            {kind === 'rule' && (
              <RuleEditor field={field} index={index} patch={props.patch}
                objects={props.objects} farForms={props.farForms}
                loadObject={props.loadObject} fields={props.fields} />
            )}
            {kind === 'calc' && (
              <CalcEditor field={field} index={index} patch={props.patch} />
            )}
            {kind === 'repeat' && (
              <RepeatEditor field={field} index={index} patch={props.patch}
                catalog={props.catalog} />
            )}
            {kind === 'sign' && (
              <SignEditor field={field} index={index} patch={props.patch} />
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 14,
            marginTop: 10, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => props.removeField(index)}
              style={linkStyle('var(--color-error)')}>Remove this field</button>
            <button type="button" onClick={() => props.duplicateField(index)}
              style={linkStyle('var(--color-text-secondary)')}>Duplicate</button>
            <button type="button"
              onClick={() => props.setAdvanced(!props.advanced)}
              style={linkStyle('var(--color-text-secondary)')}>
              {props.advanced ? 'Hide advanced' : 'Advanced'}
            </button>
            <span style={{ flex: 1 }} />
            <span style={hintStyle}>
              Used in the body as <code>{field.key}</code>
            </span>
          </div>

          {props.advanced && (
            <AdvancedEditor field={field} index={index} patch={props.patch} />
          )}
        </div>
      )}
    </div>
  )
}

// ── the six editors ────────────────────────────────────────────────────────

type Patch = (index: number, fn: (f: FieldRow) => void) => void

const DATA_TYPES = ['VARCHAR', 'NUMERIC', 'DATE', 'BOOLEAN', 'USER', 'IMAGE',
  'FILE', 'JSON']

function EntryEditor({ field, index, group, patch }: {
  field: FieldRow; index: number; group: FieldGroup
  groups: FieldGroup[]; patch: Patch
}) {
  const scope = useContext(StoredInContext)
  // A column of a real transaction already HAS a type, and the document does
  // not get a second opinion: the engine ships the document's own `data_type`
  // to the client, so the two disagreeing renders the wrong control and — on
  // the document-data path — writes the value into the wrong typed column.
  const owned = scope.model
    ? typeIsOwned(destinationIn(
      field,
      scope.model.form.templates[scope.templateIndex || 0]?.fields || [],
      scope.model.form.transactions || [], scope.catalog))
    : false
  return (
    <>
      <Line>
        <span>{group.detail ? 'The operator' : 'The person filling the form'}
          {' '}types this in as</span>
        {owned ? (
          <span style={{ ...inputStyle, width: 150, display: 'inline-flex',
            alignItems: 'center', cursor: 'not-allowed',
            background: 'var(--color-bg-secondary)',
            color: 'var(--color-text-secondary)' }}
            title="The transaction column decides this type">
            {TYPE_WORDS[String(field.data_type).toUpperCase()]
              || field.data_type}
          </span>
        ) : (
          <select value={field.data_type}
            onChange={(e) => patch(index,
              (f) => { f.data_type = e.target.value })}
            style={{ ...inputStyle, width: 150 }}>
            {DATA_TYPES.map((t) => (
              <option key={t} value={t}>{TYPE_WORDS[t] || t}</option>
            ))}
          </select>
        )}
        <span>and it is</span>
        <select value={field.mandatory ? 'yes' : 'no'}
          disabled={!!field.protect}
          onChange={(e) => patch(index,
            (f) => { f.mandatory = e.target.value === 'yes' })}
          style={{ ...inputStyle, width: 130 }}>
          <option value="no">optional</option>
          <option value="yes">required</option>
        </select>
      </Line>

      {/* Read-only, in the transaction column's sense of the word: the value is
          shown and cannot be typed over. Not the same as making the field a
          merge — this one is still loaded, still validated and still written
          back, which is what a locked field on a document has to be. */}
      <Line>
        <span>and the operator</span>
        <select value={field.protect ? 'locked' : 'types'}
          onChange={(e) => patch(index, (f) => {
            f.protect = e.target.value === 'locked'
            // A box nobody can type into cannot be "required of a person":
            // the requirement would be unsatisfiable on an empty record and
            // the save would refuse with no way to comply.
            if (f.protect) f.mandatory = false
          })}
          style={{ ...inputStyle, width: 210 }}>
          <option value="types">may type into it</option>
          <option value="locked">sees it, and cannot change it</option>
        </select>
        {field.protect && (
          <span style={hintStyle}>
            Shown from wherever it is stored, and written by nothing — so what
            holds the value keeps it.
          </span>
        )}
      </Line>

      <BoundStoredIn field={field}
        onChange={(fn) => patch(index, fn)} heading="Where it is saved" />

      {String(field.data_type).toUpperCase() === 'NUMERIC' && (
        <Line>
          <span>In tolerance between</span>
          <input type="number" value={field.tolerance?.min ?? ''}
            placeholder="no floor"
            onChange={(e) => patch(index, (f) => {
              f.tolerance = boundOf(f.tolerance, 'min', e.target.value)
            })}
            style={{ ...inputStyle, width: 110 }} />
          <span>and</span>
          <input type="number" value={field.tolerance?.max ?? ''}
            placeholder="no ceiling"
            onChange={(e) => patch(index, (f) => {
              f.tolerance = boundOf(f.tolerance, 'max', e.target.value)
            })}
            style={{ ...inputStyle, width: 110 }} />
          <span style={hintStyle}>
            The band is printed on the record, so the operator knows it before
            they type.
          </span>
        </Line>
      )}
    </>
  )
}

function ContextEditor({ field, index, group, groups, patch }: {
  field: FieldRow; index: number; group: FieldGroup
  groups: FieldGroup[]; patch: Patch
}) {
  const binding = (field.binding || {}) as Record<string, unknown>
  const anchored = String(binding.mode || '') === 'context'

  /*
   * A CRITERION has nothing to choose.
   *
   * `KEY.<NAME>` names the document's identity, not a form and a column, so
   * the "saved into" control below has no target to offer it — and its
   * `<select>`, finding no option matching the path, would show the first one
   * and rewrite the map to a transaction column the moment it was touched.
   * The honest control here is a sentence.
   */
  if (group.id === CRITERIA_GROUP) {
    const [, name] = parsePath(field.map)
    return (
      <div style={{ display: 'grid', gap: 7 }}>
        <span style={microLabel}>SHOWN FROM</span>
        <span style={{ fontSize: '0.9225rem' }}>
          The criterion <strong>{name || field.key}</strong>, answered when the
          run was started. One value for the whole document.
        </span>
        <code style={{ fontSize: '0.8125rem',
          color: 'var(--color-text-faint)' }}>{field.map}</code>
        <span style={hintStyle}>
          Remove it here to take it off the document. Which criteria exist is
          the visual&rsquo;s own business, not this form&rsquo;s.
        </span>
      </div>
    )
  }

  return (
    <>
      <label style={checkRow}>
        <input type="checkbox" checked={anchored}
          onChange={(e) => patch(index, (f) => {
            if (e.target.checked) {
              const [, , column] = parsePath(f.map)
              f.binding = { ...(f.binding || {}), mode: 'context',
                column: (column || f.key).toLowerCase() }
            } else {
              f.binding = null
            }
          })} />
        <span>
          Read it straight off the record this document was opened for
          <span style={{ ...hintStyle, display: 'block' }}>
            Otherwise it shows whatever the form and column below already hold
            on the loaded record.
          </span>
        </span>
      </label>

      {anchored ? (
        <Line>
          <span>Take</span>
          <input value={String(binding.column || '')}
            placeholder="a column of the record"
            onChange={(e) => patch(index, (f) => {
              f.binding = { ...(f.binding || {}), mode: 'context',
                column: e.target.value }
            })}
            style={{ ...inputStyle, width: 220,
              fontFamily: 'var(--font-mono)' }} />
          <span>and show it as</span>
          <input value={String(binding.format || '')}
            placeholder="however it is stored"
            onChange={(e) => patch(index, (f) => {
              f.binding = { ...(f.binding || {}), mode: 'context',
                format: e.target.value }
            })}
            style={{ ...inputStyle, width: 180 }} />
        </Line>
      ) : (
        <SavedInto field={field} index={index} group={group} groups={groups}
          patch={patch} label="SHOWN FROM" />
      )}
    </>
  )
}

function RuleEditor({ field, index, patch, objects, farForms, loadObject,
  fields }: {
  field: FieldRow; index: number; patch: Patch
  objects: { obj_name: string }[]
  farForms: Record<string, { formNo: string; title: string;
    detail: boolean; columns: SourceColumn[] }[]>
  loadObject: (name: string) => void
  fields: FieldRow[]
}) {
  const binding = (field.binding || {}) as Record<string, unknown>
  const source = String(binding.source_object || '').toUpperCase()
  const formNo = String(binding.source_form || '1')
  const forms = farForms[source] || []
  // The object a binding already names is not one of the form's transactions,
  // so nothing else would ever fetch it.
  useEffect(() => { if (source) loadObject(source) }, [source, loadObject])
  const form = forms.find((f) => f.formNo === formNo) || forms[0]
  const columns = form?.columns || []
  const fill = Array.isArray(binding.fill)
    ? (binding.fill as Record<string, unknown>[]) : []

  const setBinding = (fn: (b: Record<string, unknown>) => void) => {
    patch(index, (f) => {
      const next = { ...(f.binding || {}), mode: 'rule' } as
        Record<string, unknown>
      fn(next)
      f.binding = next
    })
  }

  return (
    <>
      <Line>
        <span>Find one</span>
        <select value={source}
          onChange={(e) => {
            const name = e.target.value
            loadObject(name)
            setBinding((b) => {
              b.source_object = name.toUpperCase()
              b.source_form = '1'
              // The match and the fills name COLUMNS of the old object; keeping
              // them would read as configured against the new one.
              b.match_column = ''
              b.fill = []
            })
          }}
          style={{ ...inputStyle, width: 230 }}>
          <option value="">— choose an object —</option>
          {objects.map((o) => (
            <option key={o.obj_name} value={o.obj_name.toUpperCase()}>
              {o.obj_name}
            </option>
          ))}
        </select>
        {forms.length > 1 && (
          <select value={formNo}
            onChange={(e) => setBinding((b) => {
              b.source_form = e.target.value
              b.match_column = ''
              b.fill = []
            })}
            style={{ ...inputStyle, width: 190 }}>
            {forms.map((f) => (
              <option key={f.formNo} value={f.formNo}>
                {f.title || `Form ${f.formNo}`}
              </option>
            ))}
          </select>
        )}
      </Line>

      <Line>
        <span>whose</span>
        <ColumnSelect columns={columns} value={String(binding.match_column || '')}
          onChange={(v) => setBinding((b) => { b.match_column = v })}
          width={200} />
        <span>matches</span>
        <select value=""
          onChange={(e) => {
            const token = e.target.value
            if (!token) return
            setBinding((b) => {
              const current = String(b.match_value || '').trim()
              b.match_value = current ? `${current}${token}` : token
            })
          }}
          style={{ ...inputStyle, width: 190 }}>
          <option value="">insert a reference…</option>
          <optgroup label="this document">
            {fields.map((f) => (
              <option key={f.key} value={`{field.${f.key}}`}>
                {f.title || f.key}
              </option>
            ))}
          </optgroup>
          <optgroup label="the record it was opened for">
            <option value="{ctx.}">{'{ctx.…} — a column of the record'}</option>
          </optgroup>
        </select>
        <input value={String(binding.match_value || '')}
          placeholder="{field.COUNTRY_CODE}"
          onChange={(e) => setBinding((b) => { b.match_value = e.target.value })}
          style={{ ...inputStyle, width: 250,
            fontFamily: 'var(--font-mono)' }} />
      </Line>

      <div style={{ display: 'grid', gap: 7 }}>
        <span style={microLabel}>AND BRING BACK</span>
        {fill.map((row, i) => (
          <Line key={i}>
            <ColumnSelect columns={columns} value={String(row.column || '')}
              onChange={(v) => setBinding((b) => {
                const list = [...(b.fill as Record<string, unknown>[] || [])]
                list[i] = { ...list[i], column: v }
                b.fill = list
              })} width={200} />
            <span>→ into</span>
            <select value={String(row.field || '')}
              onChange={(e) => setBinding((b) => {
                const list = [...(b.fill as Record<string, unknown>[] || [])]
                list[i] = { ...list[i], field: e.target.value }
                b.fill = list
              })}
              style={{ ...inputStyle, width: 210 }}>
              <option value="">— choose a field —</option>
              {fields.map((f) => (
                <option key={f.key} value={f.key}>{f.title || f.key}</option>
              ))}
            </select>
            <button type="button"
              onClick={() => setBinding((b) => {
                b.fill = (b.fill as unknown[] || []).filter((_, j) => j !== i)
              })}
              style={linkStyle('var(--color-text-faint)')}>remove</button>
          </Line>
        ))}
        <button type="button"
          onClick={() => setBinding((b) => {
            b.fill = [...(b.fill as unknown[] || []),
              { field: field.key, column: '' }]
          })}
          style={linkStyle('var(--color-accent)')}>
          + Bring back another column
        </button>
        {!fill.length && (
          <span style={hintStyle}>
            With nothing listed, the field takes the matched row&rsquo;s column
            of its own name.
          </span>
        )}
      </div>

      <Line>
        <span>If nothing matches</span>
        <select value={String(binding.on_no_match || 'blank')}
          onChange={(e) => setBinding((b) => { b.on_no_match = e.target.value })}
          style={{ ...inputStyle, width: 200 }}>
          <option value="blank">leave it blank</option>
          <option value="leave">keep what is there</option>
          <option value="error">stop and say so</option>
        </select>
        <span>If more than one matches</span>
        <select value={String(binding.on_multi_match || 'error')}
          onChange={(e) => setBinding((b) => {
            b.on_multi_match = e.target.value
          })}
          style={{ ...inputStyle, width: 200 }}>
          <option value="error">stop and say so</option>
          <option value="warn">take the first, and log it</option>
        </select>
      </Line>
    </>
  )
}

function CalcEditor({ field, index, patch }: {
  field: FieldRow; index: number; patch: Patch
}) {
  return (
    <>
      <Line>
        <span>Work it out as</span>
        <input value={field.calc || ''}
          placeholder="sum({INVOICE.2.AMOUNT})"
          onChange={(e) => patch(index, (f) => { f.calc = e.target.value })}
          style={{ ...inputStyle, width: 420,
            fontFamily: 'var(--font-mono)', fontSize: '0.8825rem' }} />
      </Line>
      <span style={hintStyle}>
        The same expression language every item_change fill and validation rule
        uses, with <code>sum</code>, <code>count</code>, <code>avg</code>,{' '}
        <code>first</code>, <code>last</code>, <code>sumif</code> and{' '}
        <code>countif</code> over a repeat. A detail column read outside a
        repeat is every row&rsquo;s value, in order.
      </span>

      {/* A calculation never takes a column by name alone — the same-key
          convention is entry-only — so without this control its answer was
          worked out, shown, and written nowhere, with nothing on screen
          saying so. */}
      <BoundStoredIn field={field}
        onChange={(fn) => patch(index, fn)} heading="Where it is saved" />
    </>
  )
}

function RepeatEditor({ field, index, patch, catalog }: {
  field: FieldRow; index: number; patch: Patch
  catalog: { ident: string; title: string;
    forms: { formNo: string; title: string; detail: boolean;
      columns: SourceColumn[] }[] }[]
}) {
  const [txn, formNo] = parsePath(field.path)
  const entry = catalog.find((c) => c.ident === txn)
  const details = (entry?.forms || []).filter((f) => f.detail)

  return (
    <>
      <Line>
        <span>Show one row for every row of</span>
        <select value={txn}
          onChange={(e) => patch(index, (f) => { f.path = `${e.target.value}.` })}
          style={{ ...inputStyle, width: 210 }}>
          <option value="">— choose a transaction —</option>
          {catalog.map((c) => (
            <option key={c.ident} value={c.ident}>{c.title}</option>
          ))}
        </select>
        <select value={formNo}
          onChange={(e) => patch(index,
            (f) => { f.path = `${txn}.${e.target.value}` })}
          style={{ ...inputStyle, width: 210 }}>
          <option value="">— choose a form —</option>
          {details.map((f) => (
            <option key={f.formNo} value={f.formNo}>
              {f.title || `Form ${f.formNo}`}
            </option>
          ))}
        </select>
      </Line>
      <Line>
        <span>ordered by</span>
        <input value={(field.order_by || []).join(', ')}
          placeholder="the order the rows are stored in"
          onChange={(e) => patch(index, (f) => {
            const parts = e.target.value.split(',').map((p) => p.trim())
              .filter(Boolean)
            f.order_by = parts.length ? parts : undefined
          })}
          style={{ ...inputStyle, width: 250 }} />
        <span>with at least</span>
        <input type="number" min={0} value={field.min_rows ?? ''}
          placeholder="0"
          onChange={(e) => patch(index, (f) => {
            const n = Number(e.target.value)
            f.min_rows = e.target.value === '' || Number.isNaN(n)
              ? undefined : n
          })}
          style={{ ...inputStyle, width: 90 }} />
        <span>rows</span>
      </Line>
      <span style={hintStyle}>
        The columns of this table are the fields saved to that form — they
        appear under its own heading in the list.
      </span>
    </>
  )
}

function SignEditor({ field, index, patch }: {
  field: FieldRow; index: number; patch: Patch
}) {
  return (
    <>
      <label style={checkRow}>
        <input type="checkbox" checked={!!field.mandatory}
          onChange={(e) => patch(index,
            (f) => { f.mandatory = e.target.checked })} />
        <span>The document cannot be submitted without this signature</span>
      </label>
      <span style={hintStyle}>
        Who may sign, and whether signing re-authenticates and states a reason,
        follows the document&rsquo;s governance — set on the Settings tab, not
        per field.
      </span>
    </>
  )
}

// ── where a field is saved — the control that MOVES it between forms ───────

function SavedInto({ field, index, group, groups, patch, label = 'SAVED INTO' }: {
  field: FieldRow; index: number; group: FieldGroup
  groups: FieldGroup[]; patch: Patch; label?: string
}) {
  const [txn, formNo, column] = parsePath(field.map)
  const currentId = txn ? `${txn}.${formNo || '1'}` : ''
  const targets = groups.filter((g) => !g.own)
  const target = targets.find((g) => g.id === currentId) || group

  const write = (nextId: string, nextColumn: string) => {
    patch(index, (f) => {
      // An empty target is the documented default — the primary's column of
      // the same name — so it clears the map. A target with no column yet
      // keeps the field on the form the author just chose.
      f.map = nextId ? `${nextId}.${nextColumn || f.key}` : ''
    })
  }

  return (
    <div style={{ display: 'grid', gap: 7 }}>
      <span style={microLabel}>{label}</span>
      <Line>
        <select value={currentId}
          onChange={(e) => write(e.target.value, column || field.key)}
          style={{ ...inputStyle, width: 260 }}>
          <option value="">
            the record this document is about, column of the same name
          </option>
          {targets.map((g) => (
            <option key={g.id} value={g.id}>
              {g.txnTitle} › {g.heading} (form {g.formNo})
            </option>
          ))}
        </select>
        {currentId && (
          <>
            <span>›</span>
            <ColumnSelect columns={target.columns} value={column}
              onChange={(v) => write(currentId, v)} width={230} />
          </>
        )}
        {field.map && (
          <code style={{ fontSize: '0.8125rem',
            color: 'var(--color-text-faint)' }}>{field.map}</code>
        )}
      </Line>
      <span style={hintStyle}>
        {group.detail
          ? 'This form repeats, so the operator fills it once on every row of '
            + 'the table.'
          : 'Change the form and the field moves to that group in the list.'}
      </span>
    </div>
  )
}

/** A column of one form, by its own heading — or free text when unknown. */
function ColumnSelect({ columns, value, onChange, width }: {
  columns: SourceColumn[]; value: string
  onChange: (v: string) => void; width: number
}) {
  if (!columns.length) {
    return (
      <input value={value} placeholder="a column"
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        style={{ ...inputStyle, width, fontFamily: 'var(--font-mono)' }} />
    )
  }
  const known = columns.some((c) => c.dbName === value.toUpperCase())
  return (
    <select value={value.toUpperCase()}
      onChange={(e) => onChange(e.target.value)}
      style={{ ...inputStyle, width }}>
      <option value="">— choose a column —</option>
      {!known && value && <option value={value.toUpperCase()}>{value}</option>}
      {columns.map((c) => (
        <option key={c.dbName} value={c.dbName}>
          {c.heading}{c.key ? ' (key)' : ''}
        </option>
      ))}
    </select>
  )
}

/**
 * The raw model, for anything the structured editors above do not know about.
 *
 * The binding round-trips as JSON deliberately: a structured editor that
 * silently drops a key it was never taught about is worse than a text box, and
 * this is the escape hatch that makes the rest of the tab safe to simplify.
 */
function AdvancedEditor({ field, index, patch }: {
  field: FieldRow; index: number; patch: Patch
}) {
  const [draft, setDraft] = useState<string | null>(null)
  const text = draft ?? (field.binding ? JSON.stringify(field.binding) : '')
  let broken = false
  if (draft !== null && draft.trim()) {
    try { JSON.parse(draft) } catch { broken = true }
  }

  return (
    <div style={{ marginTop: 10, padding: 12, borderRadius: 8,
      border: '1px dashed var(--color-border)', display: 'grid', gap: 10 }}>
      <Line>
        <span style={microLabel}>SAVED AT</span>
        <input value={field.map || ''} placeholder="TXN.form.COLUMN"
          onChange={(e) => patch(index, (f) => { f.map = e.target.value })}
          style={{ ...inputStyle, width: 240,
            fontFamily: 'var(--font-mono)' }} />
        <span style={microLabel}>A COLUMN OF TABLE</span>
        <input value={field.in_table || ''} placeholder="the repeat's key"
          onChange={(e) => patch(index, (f) => { f.in_table = e.target.value })}
          style={{ ...inputStyle, width: 180,
            fontFamily: 'var(--font-mono)' }} />
      </Line>
      <Line>
        {/* Q10. The same expression language a formula uses, so an author who
            can write one can write the other — and empty means "always",
            which is what every field authored before this said by saying
            nothing. A field whose rule is false prints struck-through N/A,
            exactly as one the operator marked so. */}
        <span style={microLabel}>APPLIES WHEN</span>
        <input value={field.relevant || ''}
          placeholder="always — or {field.NEEDS_VISA} == 'Y'"
          onChange={(e) => patch(index, (f) => { f.relevant = e.target.value })}
          style={{ ...inputStyle, width: 260,
            fontFamily: 'var(--font-mono)' }} />
        {/* Gap B. "" keeps today's inference — the primary has a column of
            this name, so it is typed into the record; otherwise it goes to the
            generic capture table. Declaring it makes both silent failures
            loud: adding a column MOVES a field out of the sink and strands its
            history, and a typo in a key routes it there instead of failing. */}
        <span style={microLabel}>CAPTURED IN</span>
        <select value={field.capture || ''}
          onChange={(e) => patch(index, (f) => { f.capture = e.target.value })}
          style={{ ...inputStyle, width: 200 }}>
          <option value="">Work it out (the default)</option>
          <option value="typed">The transaction's own column</option>
          <option value="sink">The generic capture table</option>
        </select>
      </Line>
      <Line>
        <span style={microLabel}>REPEATS OVER</span>
        <input value={field.path || ''} placeholder="TXN.form"
          onChange={(e) => patch(index, (f) => { f.path = e.target.value })}
          style={{ ...inputStyle, width: 180,
            fontFamily: 'var(--font-mono)' }} />
        <span style={microLabel}>ROWS WHERE</span>
        <input value={field.filter || ''} placeholder="a condition"
          onChange={(e) => patch(index, (f) => { f.filter = e.target.value })}
          style={{ ...inputStyle, width: 240,
            fontFamily: 'var(--font-mono)' }} />
      </Line>
      <div style={{ display: 'grid', gap: 5 }}>
        <span style={microLabel}>BINDING</span>
        <textarea rows={3} value={text}
          onChange={(e) => {
            setDraft(e.target.value)
            const raw = e.target.value.trim()
            if (!raw) { patch(index, (f) => { f.binding = null }); return }
            try {
              const parsed = JSON.parse(raw)
              patch(index, (f) => {
                f.binding = parsed as Record<string, unknown>
              })
            } catch {
              // Left alone while the author is mid-type; the draft above is
              // what they see, and the model keeps the last good value.
            }
          }}
          onBlur={() => setDraft(null)}
          placeholder='no binding — e.g. {"mode":"context","column":"…"}'
          style={{ ...inputStyle, width: '100%', height: 'auto',
            padding: 8, fontFamily: 'var(--font-mono)', fontSize: '0.8425rem',
            borderColor: broken ? 'var(--color-error)'
              : 'var(--color-border)' }} />
        {broken && (
          <span style={{ ...hintStyle, color: 'var(--color-error)' }}>
            Not valid JSON yet — the model still holds the last good value.
          </span>
        )}
      </div>
    </div>
  )
}

// ── the resolve pass, as a review ──────────────────────────────────────────

function PreviewSummary({ preview, verdicts, total, forms }: {
  preview: PreviewResult | null
  verdicts: Record<string, { verdict: Verdict; text: string }>
  total: number
  forms: number
}) {
  if (!preview) {
    return (
      <span style={hintStyle}>
        {total === 1 ? '1 field' : `${total} fields`}
        {forms ? ` across ${forms === 1 ? '1 form' : `${forms} forms`}` : ''}
        {' '}— open one to say where its value comes from.
      </span>
    )
  }
  const list = Object.values(verdicts)
  const bad = list.filter((v) => v.verdict === 'bad').length
  const ok = list.filter((v) => v.verdict === 'ok').length
  const wait = list.filter((v) => v.verdict === 'wait').length
  return (
    <span style={{ fontSize: '0.9225rem',
      color: bad ? 'var(--color-error)' : 'var(--color-text)' }}>
      {bad
        ? `${bad === 1 ? 'One field' : `${bad} fields`} could not be filled — `
        : 'Everything resolved — '}
      {ok} came back, {wait} wait for the person filling the form.
    </span>
  )
}

/** The preview, turned into one verdict per field key. */
function verdictsOf(preview: PreviewResult | null, fields: FieldRow[]):
Record<string, { verdict: Verdict; text: string }> {
  if (!preview) return {}
  const out: Record<string, { verdict: Verdict; text: string }> = {}
  const values = preview.values || {}

  fields.forEach((field) => {
    const kind = sourceOf(field)
    if (kind === 'entry' || kind === 'sign') {
      out[field.key] = { verdict: 'wait',
        text: kind === 'sign' ? 'Signed at submission'
          : 'Waiting for the operator' }
      return
    }
    const value = values[field.key]
    if (value === undefined || value === null || value === '') {
      out[field.key] = { verdict: 'wait', text: 'Nothing came back' }
      return
    }
    out[field.key] = { verdict: 'ok', text: describeValue(value) }
  })

  // A denial and an unresolved binding both override whatever the values said:
  // a document that shows a blank where an identity may not read is how
  // somebody signs a record they think is complete.
  ;(preview.unresolved || []).forEach((u) => {
    out[u.field] = { verdict: 'bad', text: u.message }
  })
  ;(preview.denied || []).forEach((d) => {
    out[d.field] = { verdict: 'bad', text: d.message || 'Refused — no rights' }
  })
  return out
}

function describeValue(value: unknown): string {
  if (Array.isArray(value)) {
    return value.length === 1 ? '1 row found' : `${value.length} rows found`
  }
  if (typeof value === 'object') return 'a row came back'
  const text = String(value)
  return text.length > 48 ? `${text.slice(0, 47)}…` : text
}

// ── small pieces ───────────────────────────────────────────────────────────

function Line({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8,
      flexWrap: 'wrap', fontSize: '0.9425rem' }}>{children}</div>
  )
}

function nextKey(fields: FieldRow[]): string {
  const taken = new Set(fields.map((f) => String(f.key || '').toUpperCase()))
  let n = fields.length + 1
  let key = `FIELD_${n}`
  while (taken.has(key)) { n += 1; key = `FIELD_${n}` }
  return key
}

function boundOf(current: FieldRow['tolerance'], which: 'min' | 'max',
  raw: string): FieldRow['tolerance'] {
  const next = { ...(current || {}) }
  next[which] = raw === '' ? null : Number(raw)
  if (next.min == null && next.max == null) return null
  return next
}

function verdictStyle(verdict: Verdict): React.CSSProperties {
  const tone = verdict === 'ok' ? 'var(--color-success)'
    : verdict === 'bad' ? 'var(--color-error)'
      : 'var(--color-text-secondary)'
  const back = verdict === 'ok' ? 'var(--color-success-weak)'
    : verdict === 'bad' ? 'var(--color-error-weak)'
      : 'var(--color-surface-2)'
  return {
    padding: '2px 8px', borderRadius: 999, fontSize: '0.8425rem',
    fontWeight: 500, color: tone, background: back,
    display: 'inline-block', maxWidth: 196, overflow: 'hidden',
    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  }
}

function chipStyle(active: boolean): React.CSSProperties {
  return {
    display: 'flex', alignItems: 'center', gap: 6, height: 26,
    padding: '0 10px', borderRadius: 999, cursor: 'pointer',
    fontSize: '0.8725rem', fontWeight: active ? 600 : 400,
    border: `1px solid ${active ? 'var(--color-accent)'
      : 'var(--color-border)'}`,
    background: active ? 'var(--color-accent-weak)' : 'var(--color-bg)',
    color: active ? 'var(--color-accent)' : 'var(--color-text-secondary)',
  }
}

function linkStyle(color: string): React.CSSProperties {
  return {
    border: 'none', background: 'transparent', padding: 0, cursor: 'pointer',
    fontSize: '0.8725rem', fontWeight: 500, color,
  }
}

const badgeStyle: React.CSSProperties = {
  padding: '1px 6px', borderRadius: 4, fontSize: '0.8125rem', fontWeight: 700,
  letterSpacing: 0.4, border: '1px solid var(--color-border)',
  background: 'var(--color-bg)', color: 'var(--color-text-secondary)',
}

const microLabel: React.CSSProperties = {
  fontSize: '0.8125rem', fontWeight: 700, letterSpacing: 0.7,
  color: 'var(--color-text-faint)',
}

const hintStyle: React.CSSProperties = {
  fontSize: '0.8425rem', color: 'var(--color-text-faint)', margin: 0,
}

const checkRow: React.CSSProperties = {
  display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: '0.9425rem',
}
